import type { GameState } from '@/lib/chess-engine';
import { analyzePosition, EnginePriority } from '@/lib/stockfish-engine';
import { cloudEvaluate } from '@/lib/cloud-eval';
import { EngineCircuitBreaker } from '@/lib/engine-circuit-breaker';
import { gameStateToFen } from '@/lib/fen-parser';
import { classifyError, NetworkError } from '@/lib/engine-errors';

export interface MoveAnalysis {
  move: string;
  evaluation: number;
  bestMove: string | null;
  accuracy: number;
  label: 'brilliant' | 'great' | 'good' | 'inaccuracy' | 'mistake' | 'blunder';
  centipawnLoss: number;
  depth: number;
  source: 'local' | 'cloud';
}

export interface GameReview {
  moves: MoveAnalysis[];
  whiteAccuracy: number;
  blackAccuracy: number;
  averageAccuracy: number;
}

// Progressive analysis configuration
const PROGRESSIVE_ANALYSIS = {
  quick: { movetime: 1000, depth: 10 },
  standard: { movetime: 2000, depth: 12 },
  deep: { movetime: 4000, depth: 15 }
} as const;

// Position cache for avoiding duplicate analysis
const positionCache = new Map<string, {
  evaluation: number;
  bestMove: string | null;
  depth: number;
  source: 'local' | 'cloud';
}>();

// Circuit breaker for engine health
const engineBreaker = EngineCircuitBreaker.getInstance();

/**
 * Analyze a single position with cloud fallback - uses NORMAL priority for batch processing
 */
async function analyzePositionWithFallback(
  fen: string,
  options: { movetime: number; depth: number },
  signal?: AbortSignal,
  priority: EnginePriority = EnginePriority.NORMAL
): Promise<{
  evaluation: number;
  bestMove: string | null;
  depth: number;
  source: 'local' | 'cloud';
}> {
  // Check cache first
  const cached = positionCache.get(fen);
  if (cached) return cached;

  try {
    // Try local analysis first if circuit breaker is closed
    if (engineBreaker.isAvailable()) {
      const result = await analyzePosition(fen, options, signal);
      
      const analysis = {
        evaluation: result.evaluation,
        bestMove: result.bestMove,
        depth: result.depth,
        source: 'local' as const
      };
      
      positionCache.set(fen, analysis);
      return analysis;
    }
  } catch (error) {
    const engineError = classifyError(error);
    console.warn('[Review Engine] Local analysis failed:', engineError.userMessage, engineError);
    
    // Don't throw yet - try cloud fallback for retryable errors
    if (!engineError.canRetry) {
      throw engineError;
    }
  }

  // Cloud fallback
  try {
    console.info('[Review Engine] Falling back to cloud evaluation...');
    const cloudResult = await cloudEvaluate(fen, 1, options.depth);
    
    if (cloudResult) {
      const analysis = {
        evaluation: cloudResult.evaluation,
        bestMove: cloudResult.bestMove,
        depth: cloudResult.depth,
        source: 'cloud' as const
      };
      
      positionCache.set(fen, analysis);
      return analysis;
    }
  } catch (error) {
    console.warn('[Review Engine] Cloud analysis failed:', error);
    throw new NetworkError(error);
  }

  throw new NetworkError(new Error('Both local and cloud analysis failed'));
}

/**
 * Determine analysis level based on game state
 */
function getAnalysisLevel(gameState: GameState, moveIndex: number): keyof typeof PROGRESSIVE_ANALYSIS {
  // Quick analysis for opening (first 10 moves)
  if (moveIndex < 10) return 'quick';
  
  // Deep analysis for endgame (few pieces remaining)
  const pieceCount = gameState.board.flat().filter(piece => piece !== null).length;
  if (pieceCount <= 8) return 'deep';
  
  // Standard for middlegame
  return 'standard';
}

/**
 * Calculate move accuracy based on centipawn loss
 */
function calculateAccuracy(centipawnLoss: number): number {
  // Chess.com-style accuracy calculation
  return Math.max(0, Math.min(100, 100 - (centipawnLoss / 100) * 10));
}

/**
 * Label move based on centipawn loss
 */
function labelMove(centipawnLoss: number): MoveAnalysis['label'] {
  if (centipawnLoss < 10) return 'brilliant';
  if (centipawnLoss < 25) return 'great';
  if (centipawnLoss < 50) return 'good';
  if (centipawnLoss < 100) return 'inaccuracy';
  if (centipawnLoss < 300) return 'mistake';
  return 'blunder';
}

/**
 * Analyze a complete game move by move with batch parallelization
 */
export async function analyzeGame(
  gameStates: GameState[],
  onProgress?: (current: number, total: number, currentlyAnalyzing?: string) => void,
  signal?: AbortSignal
): Promise<GameReview> {
  if (gameStates.length < 2) {
    throw new Error('Need at least 2 game states to analyze');
  }

  const moves: MoveAnalysis[] = [];
  let whiteTotal = 0, blackTotal = 0;
  let whiteCount = 0, blackCount = 0;

  // Batch configuration
  const BATCH_SIZE = 3; // Analyze 3 positions simultaneously
  const BATCH_DELAY = 100; // Small delay between batches to avoid overwhelming engine
  
  // Pre-analyze all positions in batches
  const totalMoves = gameStates.length - 1;
  const positionAnalyses: Array<{
    evaluation: number;
    bestMove: string | null;
    depth: number;
    source: 'local' | 'cloud';
  } | null> = new Array(gameStates.length).fill(null);

  // Batch analyze positions
  for (let batchStart = 0; batchStart < gameStates.length; batchStart += BATCH_SIZE) {
    if (signal?.aborted) {
      throw new DOMException('Analysis cancelled', 'AbortError');
    }

    const batchEnd = Math.min(batchStart + BATCH_SIZE, gameStates.length);
    const batchPromises: Promise<void>[] = [];

    for (let i = batchStart; i < batchEnd; i++) {
      const gameState = gameStates[i];
      const fen = gameStateToFen(gameState);
      
      // Determine analysis level
      const analysisLevel = getAnalysisLevel(gameState, i);
      const options = PROGRESSIVE_ANALYSIS[analysisLevel];

      // Update progress with detailed info
      onProgress?.(
        i, 
        totalMoves, 
        `Analyzing move ${i + 1}/${gameStates.length} (batch ${Math.floor(batchStart / BATCH_SIZE) + 1}/${Math.ceil(gameStates.length / BATCH_SIZE)})`
      );

      const promise = analyzePositionWithFallback(fen, options, signal, EnginePriority.NORMAL)
        .then(result => {
          positionAnalyses[i] = result;
        })
        .catch(error => {
          console.error(`[Review Engine] Failed to analyze position ${i}:`, error);
          // Leave as null, will handle in next phase
        });

      batchPromises.push(promise);
    }

    // Wait for current batch to complete
    await Promise.all(batchPromises);

    // Small delay between batches for engine breathing room
    if (batchEnd < gameStates.length) {
      await new Promise(resolve => setTimeout(resolve, BATCH_DELAY));
    }
  }

  // Calculate move quality based on position evaluations
  for (let i = 1; i < gameStates.length; i++) {
    if (signal?.aborted) {
      throw new DOMException('Analysis cancelled', 'AbortError');
    }

    const previousState = gameStates[i - 1];
    const currentState = gameStates[i];
    
    const beforeAnalysis = positionAnalyses[i - 1];
    const afterAnalysis = positionAnalyses[i];

    if (!beforeAnalysis || !afterAnalysis) {
      // Failed analysis - add placeholder
      moves.push({
        move: `Move ${i}`,
        evaluation: 0,
        bestMove: null,
        accuracy: 50,
        label: 'good',
        centipawnLoss: 0,
        depth: 0,
        source: 'local'
      });
      continue;
    }

    try {
      // Calculate centipawn loss (from perspective of player who moved)
      const isWhiteMove = previousState.sideToMove === 'white';
      const beforeEval = isWhiteMove ? beforeAnalysis.evaluation : -beforeAnalysis.evaluation;
      const afterEval = isWhiteMove ? -afterAnalysis.evaluation : afterAnalysis.evaluation;
      
      const centipawnLoss = Math.max(0, beforeEval - afterEval);
      const accuracy = calculateAccuracy(centipawnLoss);
      
      // Track accuracy by player
      if (isWhiteMove) {
        whiteTotal += accuracy;
        whiteCount++;
      } else {
        blackTotal += accuracy;
        blackCount++;
      }

      moves.push({
        move: `Move ${i}`,
        evaluation: afterAnalysis.evaluation,
        bestMove: beforeAnalysis.bestMove,
        accuracy,
        label: labelMove(centipawnLoss),
        centipawnLoss,
        depth: beforeAnalysis.depth,
        source: beforeAnalysis.source
      });

    } catch (error) {
      console.error(`[Review Engine] Failed to process move ${i}:`, error);
      
      // Add placeholder move to keep the analysis going
      moves.push({
        move: `Move ${i}`,
        evaluation: afterAnalysis.evaluation,
        bestMove: beforeAnalysis.bestMove,
        accuracy: 50,
        label: 'good',
        centipawnLoss: 0,
        depth: beforeAnalysis.depth,
        source: beforeAnalysis.source
      });
    }
  }

  const whiteAccuracy = whiteCount > 0 ? whiteTotal / whiteCount : 0;
  const blackAccuracy = blackCount > 0 ? blackTotal / blackCount : 0;
  const averageAccuracy = (whiteAccuracy + blackAccuracy) / 2;

  onProgress?.(totalMoves, totalMoves, 'Analysis complete');

  return {
    moves,
    whiteAccuracy: Math.round(whiteAccuracy),
    blackAccuracy: Math.round(blackAccuracy),
    averageAccuracy: Math.round(averageAccuracy)
  };
}
